import { IMAGE_ALTS } from "./seoDefaults";
// Utility SEO lato client: aggiorna title, meta description, canonical e JSON-LD.
// I crawler moderni (Google) eseguono il JS e leggono questi tag renderizzati.

function upsertMeta(name, content) {
  let el = document.head.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content || "");
}

function upsertMetaProp(property, content) {
  let el = document.head.querySelector(`meta[property="${property}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("property", property);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content || "");
}

function upsertLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!href) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export function setPageMeta({ title, description, canonical } = {}) {
  if (title) document.title = title;
  if (description !== undefined) upsertMeta("description", description);
  if (canonical !== undefined) upsertLink("canonical", canonical);
}

// Imposta (o rimuove) il meta robots. Usa "noindex, nofollow" per le pagine
// rimosse/non trovate così Google le deindicizza; passa null per ripristinare l'indicizzazione.
export function setRobots(content) {
  const el = document.head.querySelector('meta[name="robots"]');
  if (!content) {
    if (el) el.remove();
    return;
  }
  upsertMeta("robots", content);
}

export function setJsonLd(id, data) {
  let el = document.getElementById(id);
  if (!data) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

const ENTITIES = {
  nbsp: " ", amp: "&", lt: "<", gt: ">", quot: '"', apos: "'", agrave: "à", aacute: "á", egrave: "è", eacute: "é",
  igrave: "ì", iacute: "í", ograve: "ò", oacute: "ó", ugrave: "ù", uacute: "ú", Agrave: "À", Egrave: "È", Eacute: "É",
  Igrave: "Ì", Ograve: "Ò", Ugrave: "Ù", laquo: "«", raquo: "»", lsquo: "‘", rsquo: "’", ldquo: "“", rdquo: "”",
  ndash: "–", mdash: "—", hellip: "…", euro: "€", deg: "°", middot: "·", bull: "•", copy: "©", reg: "®", trade: "™",
};

function decodeEntities(s) {
  return s.replace(/&(#x[0-9a-f]+|#\d+|[a-z]+);/gi, (m, e) => {
    if (e[0] === "#") {
      const code = e[1] === "x" || e[1] === "X" ? parseInt(e.slice(2), 16) : parseInt(e.slice(1), 10);
      return Number.isFinite(code) ? String.fromCodePoint(code) : m;
    }
    return ENTITIES[e] ?? m;
  });
}

// Testo semplice da HTML senza DOM (funziona anche in pre-rendering Node).
export function htmlToText(html) {
  const s = (html || "")
    .replace(/<!--[\s\S]*?-->/g, " ")
    .replace(/<(style|script)\b[\s\S]*?<\/\1>/gi, " ")
    .replace(/<br\s*\/?>/gi, " ")
    .replace(/<\/(p|div|h[1-6]|li|tr|td|th|blockquote)>/gi, " ")
    .replace(/<[^>]+>/g, "");
  return decodeEntities(s).replace(/[\s\u200b]+/g, " ").trim();
}

// Testo semplice dal contenuto HTML: spazi compressi, troncato a max caratteri su parola intera.
export function stripHtml(html, max = 155) {
  const text = htmlToText(html);
  if (text.length <= max) return text;
  const cut = text.slice(0, max + 1);
  const i = cut.lastIndexOf(" ");
  return (i > 0 ? cut.slice(0, i) : text.slice(0, max)).replace(/[\s,;:.\-–—]+$/, "");
}

// Rimuove il markup superfluo incollato da Word/Office (commenti condizionali, <style>, <xml>).
export function cleanHtml(html) {
  return (html || "")
    .replace(/<!--[\s\S]*?-->/g, "")
    .replace(/<(style|xml)\b[\s\S]*?<\/\1>/gi, "");
}

// Fallback meta description: solo frasi compiute del primo paragrafo sostanzioso (mai titoli concatenati).
export function autoDescription(html, max = 155) {
  const paras = [...(html || "").matchAll(/<p\b[^>]*>([\s\S]*?)<\/p>/gi)].map((m) => htmlToText(m[1])).filter((t) => t.length >= 100);
  const text = paras[0] || "";
  if (!text) return stripHtml(html, max);
  if (text.length <= max) return text;
  let out = "";
  for (const s of text.match(/[^.!?]+[.!?]+(\s|$)/g) || []) {
    if ((out + s).trim().length > max) break;
    out += s;
  }
  out = out.trim();
  return out.length >= 80 ? out : stripHtml(text, max);
}

// Domande e risposte visibili: titoli che terminano con "?" seguiti dal testo fino al titolo successivo.
export function extractFaq(html) {
  const src = html || "";
  const re = /<h([1-6])\b[^>]*>([\s\S]*?)<\/h\1>/gi;
  const heads = [];
  let m;
  while ((m = re.exec(src))) heads.push({ level: +m[1], text: htmlToText(m[2]), start: m.index, end: re.lastIndex });
  const out = [];
  heads.forEach((h, i) => {
    if (!h.text.endsWith("?")) return;
    const next = heads[i + 1];
    let answer = htmlToText(src.slice(h.end, next ? next.start : src.length));
    if (!answer) {
      const sib = heads.slice(i + 1).find((n) => n.level <= h.level);
      answer = htmlToText(src.slice(h.end, sib ? sib.start : src.length));
    }
    if (answer) out.push({ question: h.text, answer });
  });
  return out;
}

// Un solo H1 per pagina: se il contenuto ha H1, abbassa tutti i titoli di un livello.
export function demoteHeadings(html) {
  if (!html || !/<h1(?=[\s>/])/i.test(html)) return html || "";
  return html.replace(/<(\/?)h([1-6])(?=[\s>/])/gi, (_, s, n) => `<${s}h${Math.min(+n + 1, 6)}`);
}

const escAttr = (s) => String(s).replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

// Alt mancanti: alt descrittivo noto (IMAGE_ALTS), altrimenti il titolo più vicino che precede l'immagine (o il fallback, es. titolo pagina).
export function addImageAlts(html, fallback = "") {
  if (!html) return "";
  let last = fallback;
  return html.replace(/<h[1-6]\b[^>]*>([\s\S]*?)<\/h[1-6]>|<img\b[^>]*>/gi, (m, inner) => {
    if (inner !== undefined) { const t = htmlToText(inner); if (t) last = t; return m; }
    const cur = m.match(/\balt\s*=\s*("([^"]*)"|'([^']*)')/i);
    if (cur && (cur[2] ?? cur[3] ?? "").trim()) return m;
    const known = Object.keys(IMAGE_ALTS).find((id) => m.includes(id));
    const alt = `alt="${escAttr(known ? IMAGE_ALTS[known] : last)}"`;
    return cur ? m.replace(cur[0], alt) : m.replace(/^<img/i, `<img ${alt}`);
  });
}

// Estrae il src della prima immagine nel contenuto HTML (per l'anteprima social).
export function firstImageSrc(html) {
  const m = (html || "").match(/<img\b[^>]*\bsrc\s*=\s*("([^"]*)"|'([^']*)')/i);
  return m ? (m[2] ?? m[3]) : null;
}

// Converte un URL eventualmente relativo in assoluto sul dominio canonico.
export function absoluteUrl(src) {
  if (!src) return "";
  if (/^https?:\/\//i.test(src)) return src;
  return `${SITE_ORIGIN}${src.startsWith("/") ? "" : "/"}${src}`;
}

// Imposta i tag Open Graph e Twitter Card per l'anteprima social.
export function setOpenGraph({ title, description, image, url, type = "website" } = {}) {
  if (type !== undefined) upsertMetaProp("og:type", type);
  if (title !== undefined) {
    upsertMetaProp("og:title", title);
    upsertMeta("twitter:title", title);
  }
  if (description !== undefined) {
    upsertMetaProp("og:description", description);
    upsertMeta("twitter:description", description);
  }
  if (image !== undefined) {
    upsertMetaProp("og:image", image);
    upsertMeta("twitter:image", image);
  }
  if (url !== undefined) upsertMetaProp("og:url", url);
}

// Dominio canonico del sito pubblico (https, senza www, senza slash finale).
export const SITE_ORIGIN = "https://jobtimehr.it";

// Costruisce l'URL canonico per un path dato (no slash finale tranne la home).
export function canonicalUrl(path = "/") {
  let p = path || "/";
  if (!p.startsWith("/")) p = "/" + p;
  if (p !== "/" && p.endsWith("/")) p = p.replace(/\/+$/, "");
  return SITE_ORIGIN + p;
}

// Canonicalizzazione lato client (equivalente 301): www->non-www, http->https,
// rimozione dello slash finale (tranne la home). Agisce solo sul dominio di produzione.
export function enforceCanonicalLocation() {
  if (typeof window === "undefined") return;
  const { hostname, pathname, search, hash, protocol } = window.location;
  const isProd = hostname === "jobtimehr.it" || hostname === "www.jobtimehr.it";
  // www -> non-www oppure http -> https: redirect reale al dominio canonico.
  if (hostname === "www.jobtimehr.it" || (isProd && protocol === "http:")) {
    let p = pathname;
    if (p !== "/" && p.endsWith("/")) p = p.replace(/\/+$/, "");
    window.location.replace("https://jobtimehr.it" + p + search + hash);
    return;
  }
  // Rimozione slash finale (tranne home) senza reload, su qualsiasi dominio.
  if (pathname !== "/" && pathname.endsWith("/")) {
    const clean = pathname.replace(/\/+$/, "");
    window.history.replaceState(null, "", clean + search + hash);
  }
}

